import { useMemo } from 'react';
import {
  ArrowRight,
  BatteryCharging,
  CheckCircle2,
  Circle,
  CircleAlert,
  Compass,
  Frame,
  Move3d,
  Radio,
  Satellite,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useFleet } from '@/store/fleet';
import { useParams } from '@/store/params';
import { cn } from '@/lib/utils';

export type AssetTab = 'summary' | 'setup' | 'calibrate' | 'params' | 'health' | 'logs';

type StepStatus = 'done' | 'attention' | 'todo' | 'unknown';

interface Step {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  detail: string;
  status: StepStatus;
  action?: { label: string; tab: AssetTab };
}

/**
 * First-time setup checklist — derived live from the loaded param table and
 * telemetry, in the order a new airframe should be brought up. "unknown"
 * means the params aren't loaded yet; nothing is guessed.
 */
export function SetupChecklist({
  robotId,
  onNavigate,
}: {
  robotId: string;
  onNavigate: (tab: AssetTab) => void;
}) {
  const params = useParams((s) => s.byRobot[robotId] ?? {});
  const state = useFleet((s) => s.states[robotId]);
  const loaded = Object.keys(params).length > 0;

  const steps = useMemo<Step[]>(() => {
    const v = (id: string) => params[id]?.value;
    const zeroed = (ids: string[]) =>
      ids.every((id) => v(id) !== undefined) && ids.every((id) => v(id) === 0);
    const p = (cond: boolean | undefined): StepStatus =>
      !loaded ? 'unknown' : cond ? 'done' : 'todo';

    const frameSet = v('FRAME_CLASS') !== undefined ? v('FRAME_CLASS') !== 0 : undefined;
    const accelDone = loaded ? !zeroed(['INS_ACCOFFS_X', 'INS_ACCOFFS_Y', 'INS_ACCOFFS_Z']) : undefined;
    const compassDone = loaded ? !zeroed(['COMPASS_OFS_X', 'COMPASS_OFS_Y', 'COMPASS_OFS_Z']) : undefined;
    const battMon = v('BATT_MONITOR') !== undefined ? v('BATT_MONITOR') !== 0 : undefined;
    const rcCal =
      v('RC1_MIN') !== undefined
        ? (v('RC1_MIN') ?? 1100) < 1150 && (v('RC1_MAX') ?? 1900) > 1850
        : undefined;
    const failsafes = loaded
      ? (v('FS_THR_ENABLE') ?? 0) !== 0 || (v('FS_GCS_ENABLE') ?? 0) !== 0
      : undefined;
    const gps3d = state?.gps ? state.gps.fix !== 'none' && state.gps.fix !== 'fix2d' : undefined;

    return [
      {
        icon: Frame,
        title: 'Frame class & type',
        detail: 'Tell the autopilot what airframe it flies — motor mixing depends on it. Reboot after.',
        status: p(frameSet),
        action: { label: 'Edit FRAME_CLASS', tab: 'params' },
      },
      {
        icon: Move3d,
        title: 'Accelerometer calibration',
        detail: 'Six-position guided calibration. Required before the EKF trusts the IMU.',
        status: p(accelDone),
        action: { label: 'Open wizard', tab: 'calibrate' },
      },
      {
        icon: Compass,
        title: 'Compass calibration',
        detail: 'Rotate-through-all-axes mag cal. Bad compass = circling loiter and flyaways.',
        status: p(compassDone),
        action: { label: 'Open wizard', tab: 'calibrate' },
      },
      {
        icon: Radio,
        title: 'RC calibration',
        detail:
          'Stick range endpoints (RC1..4 MIN/MAX). Skip only for pure GCS-controlled vehicles.',
        status: !loaded ? 'unknown' : rcCal ? 'done' : 'attention',
        action: { label: 'Review RC params', tab: 'params' },
      },
      {
        icon: BatteryCharging,
        title: 'Battery monitor',
        detail: 'Voltage/current sensing enables failsafes and capacity estimates.',
        status: p(battMon),
        action: { label: 'Edit BATT_MONITOR', tab: 'params' },
      },
      {
        icon: ShieldCheck,
        title: 'Failsafes',
        detail: 'RC-loss and GCS-loss behaviour, battery thresholds, geofence.',
        status: !loaded ? 'unknown' : failsafes ? 'done' : 'attention',
        action: { label: 'Run config check', tab: 'params' },
      },
      {
        icon: Satellite,
        title: 'GPS 3D fix',
        detail: 'Live check — needs sky view. Watch sat count + fix type in Health.',
        status: gps3d === undefined ? 'unknown' : gps3d ? 'done' : 'todo',
        action: { label: 'Open health', tab: 'health' },
      },
    ];
  }, [params, state, loaded]);

  const done = steps.filter((s) => s.status === 'done').length;

  return (
    <div className="mx-auto max-w-[760px] p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-fg-primary">First-time setup</h2>
          <p className="text-[11.5px] text-fg-muted">
            {loaded
              ? `${done} of ${steps.length} steps look complete — derived from the live param table.`
              : 'Load the param table (Params → Load all) to evaluate these steps.'}
          </p>
        </div>
        <div className="font-mono text-[22px] font-semibold text-accent">
          {loaded ? `${Math.round((done / steps.length) * 100)}%` : '—'}
        </div>
      </div>

      <ol className="space-y-2">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <li
              key={s.title}
              className={cn(
                'flex items-center gap-3 rounded-lg border px-3 py-2.5',
                s.status === 'done' && 'border-status-good/30 bg-status-good/5',
                s.status === 'attention' && 'border-status-warn/30 bg-status-warn/5',
                (s.status === 'todo' || s.status === 'unknown') &&
                  'border-border-default bg-surface-1',
              )}
            >
              <StatusGlyph status={s.status} />
              <Icon className="h-4 w-4 flex-none text-fg-secondary" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-fg-primary">{s.title}</div>
                <div className="text-[11px] leading-snug text-fg-muted">{s.detail}</div>
              </div>
              {s.action && s.status !== 'done' && (
                <Button size="sm" variant="secondary" onClick={() => onNavigate(s.action!.tab)}>
                  {s.action.label}
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StatusGlyph({ status }: { status: StepStatus }) {
  if (status === 'done') return <CheckCircle2 className="h-5 w-5 flex-none text-status-good" />;
  if (status === 'attention') return <CircleAlert className="h-5 w-5 flex-none text-status-warn" />;
  return (
    <Circle
      className={cn('h-5 w-5 flex-none', status === 'unknown' ? 'text-fg-disabled' : 'text-fg-muted')}
    />
  );
}
